import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Trash2, RefreshCw, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "usl_admin_token";
const STATUSES = ["New", "Scheduled", "Completed", "No-Answer", "Declined"];
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

function StatusPill({ status }) {
  const tones = {
    New: "border-[#C9A24B]/50 text-[#C9A24B]",
    Scheduled: "border-blue-400/40 text-blue-300",
    Completed: "border-emerald-400/40 text-emerald-300",
    "No-Answer": "border-amber-400/40 text-amber-300",
    Declined: "border-rose-400/40 text-rose-300",
  };
  return (
    <span className={`text-[10px] tracking-[0.18em] uppercase font-semibold border px-2.5 py-1 ${tones[status] || "border-[#F4EFE6]/20 text-[#9BA39B]"}`}>
      {status}
    </span>
  );
}

function Row({ c, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);

  const setStatus = async (status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/callback-requests/${c.id}`, { status }, { headers: authHeader() });
      onUpdate(data);
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete callback request from ${c.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/callback-requests/${c.id}`, { headers: authHeader() });
      onDelete(c.id);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`callback-row-${c.id}`} className="border-b border-[#F4EFE6]/10">
      <div className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm">
        <div className="col-span-3">
          <div className="text-[#F4EFE6] font-semibold">{c.name}</div>
          <div className="text-[11px] text-[#9BA39B] mt-1 flex flex-wrap gap-3">
            <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-[#C9A24B]"><Phone size={11} /> {c.phone}</a>
            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-[#C9A24B]"><Mail size={11} /> {c.email}</a>}
          </div>
        </div>
        <div className="col-span-3 text-[#E8E1D2]">
          <div className="text-[13px]">{c.preferred_day}</div>
          <div className="text-[11px] text-[#9BA39B]">{c.preferred_time}</div>
        </div>
        <div className="col-span-2 text-[#9BA39B] text-[12px]">{c.location || "—"}</div>
        <div className="col-span-2"><StatusPill status={c.status} /></div>
        <div className="col-span-2 flex items-center justify-end gap-2">
          <select data-testid={`callback-status-${c.id}`} value={c.status} onChange={(e) => setStatus(e.target.value)}
            className="bg-[#0B0F0E] border border-[#F4EFE6]/15 text-[#F4EFE6] text-[11px] px-2 py-1.5 tracking-wider uppercase">
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <button data-testid={`callback-expand-${c.id}`} onClick={() => setOpen(!open)} className="text-[#9BA39B] hover:text-[#C9A24B] p-1.5">
            <ChevronDown size={16} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
          </button>
          <button data-testid={`callback-delete-${c.id}`} onClick={remove} className="text-[#9BA39B] hover:text-rose-400 p-1.5"><Trash2 size={16} /></button>
        </div>
      </div>
      {open && (
        <div className="px-5 pb-5 grid grid-cols-12 gap-4 text-[13px]">
          <div className="col-span-6">
            <div className="overline mb-1">Notes from caller</div>
            <p className="text-[#E8E1D2] whitespace-pre-wrap">{c.notes || "—"}</p>
          </div>
          <div className="col-span-6 text-[#9BA39B] text-[11px]">
            <div>Created: {new Date(c.created_at).toLocaleString("en-GB")}</div>
            <div className="mt-1">ID: <span className="font-mono text-[#C9A24B]">{c.id.slice(0, 8)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Callbacks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/callback-requests`, { headers: authHeader() });
      setItems(data);
    } catch { toast.error("Failed to load callbacks"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "All" ? items : items.filter(i => i.status === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="overline mr-2">Status:</div>
        {["All", ...STATUSES].map(s => (
          <button key={s} data-testid={`callback-filter-${s}`} onClick={() => setFilter(s)}
            className={`text-[11px] tracking-[0.18em] uppercase font-semibold border px-3 py-1.5 ${filter === s ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#C9A24B]" : "border-[#F4EFE6]/15 text-[#9BA39B] hover:border-[#C9A24B]/50"}`}
          >{s}</button>
        ))}
        <div className="ml-auto">
          <button data-testid="callback-reload" onClick={load} className="usl-btn-outline text-xs"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="border border-[#F4EFE6]/10 bg-[#11171A]">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] tracking-[0.24em] uppercase text-[#9BA39B] font-semibold border-b border-[#F4EFE6]/10">
          <div className="col-span-3">Name / Contact</div>
          <div className="col-span-3">Preferred</div>
          <div className="col-span-2">Location</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-[#9BA39B] text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div data-testid="callbacks-empty" className="p-10 text-center text-[#9BA39B] text-sm">No callback requests yet.</div>
        ) : filtered.map(c => (
          <Row key={c.id} c={c}
            onUpdate={(u) => setItems(items.map(x => x.id === u.id ? u : x))}
            onDelete={(id) => setItems(items.filter(x => x.id !== id))}
          />
        ))}
      </div>
    </div>
  );
}
